import * as React from "react";
import { ChoiceGroup, IChoiceGroupOption, IChoiceGroupStyles } from "@fluentui/react";
import * as Messages from "../../codegen/Messages";
import { buildTestId, ComponentTestIds } from "../../helpers/testIdHelper";
import { BaseInputProps } from "./BaseInput";
import {
  FormContext,
  FormGroupValidationModeContext,
  FormInputGroupLayoutContext,
  FormState,
  FormValidationMode,
  FormValidationModeContext,
  ValidationState,
} from "./Form";
import { buildLabelWrapperTestIds, LabelWrapper, LabelWrapperTestIds } from "./LabelWrapper";

// tslint:disable-next-line: interface-name
export interface RadioGroupOption {
  /**
   * Whether or not the option is selected
   */
  selected?: boolean;
  /**
   * Unique identifier for the radio group option
   */
  id: string;
  /**
   * Text to render for this option
   */
  text: string;
  /**
   * Disables the given option
   * @default false
   */
  disabled?: boolean
  /**
   * data-test-id HTML attribute value
   */
  testId?: string;
}

export interface RadioGroupProps extends BaseInputProps {
  /**
   * Label for the RadioGroup field
   */
  label?: string;
  /**
   *Options to be listed for selection
   */
  options: RadioGroupOption[];
  /**
   * Value to be selected intially
   */
  defaultValue: string;
  /**
   * Callback when an option is selected or existing selection is changed
   */
  onChange?: (key: string) => void;
}

const radioGroupStyles: Partial<IChoiceGroupStyles> = {
  root: { width: "100%" },
  flexContainer: "horizontal",
};

export type RadioGroupTestIds = ComponentTestIds & LabelWrapperTestIds;

export const buildRadioGroupTestIds = (testId?: string): RadioGroupTestIds => ({
  ...buildLabelWrapperTestIds(testId),
  component: buildTestId(testId),
});

/**
 * RadioGroup component  lets the user select a single option from two or more choices.
 */
export const RadioGroup = ({
  testId,
  fieldName,
  groupName,
  label,
  defaultValue,
  disabled,
  options,
  tooltip,
  required,
  statusInfo,
  subField,
  onChange,
}: RadioGroupProps): JSX.Element => {
  const { groupName: layoutGroup, layout } = React.useContext(
    FormInputGroupLayoutContext,
  );
  const group = groupName || layoutGroup;
  const validationMode = React.useContext(FormValidationModeContext);
  const groupValidationMode = React.useContext(FormGroupValidationModeContext);
  const form: FormState = React.useContext(FormContext);
  if (!Object.keys(form).length) {
    throw new Error("RadioGroup should be used within form");
  }

  const [errorMessages, setErrorMessages] = React.useState<string[]>([]);

  const radioGroupTestIds = buildRadioGroupTestIds(testId);

  const radioGroupOptions = options.map(option => ({
    key: option.id,
    text: option.text,
    disabled: disabled || option.disabled,
    "data-test-id": option.testId,
  } as IChoiceGroupOption));

  React.useEffect(() => {
    form.registerFieldLabel(fieldName, label);
    form.registerFieldGroup(fieldName, group);

    if (required) {
      form.registerField(fieldName, group);
    }
    if (defaultValue) {
      const selectedOptions = options?.find(option => option.id === defaultValue);
      form.setValue(selectedOptions, fieldName, group);
      fieldValidation();
    }
    return function cleanup() {
      if (required) {
        form.unRegisterField(fieldName, group);
      }
    };
  // Only at mount time. Others props should not have side effect beyond their initial values
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const internalOnChange = (
    _?: React.FormEvent<HTMLElement | HTMLInputElement>,
    option?: IChoiceGroupOption,
  ): void => {
    const newSelection: RadioGroupOption = { id: option?.key as string, text: option?.text as string };
    form.setValue(newSelection, fieldName, group);

    if (required) {
      fieldValidation();
    }

    onChange?.(option?.key || "");
  };

  const fieldValidation = (): void => {
    const errors: string[] = [];

    if (required && !form.getValue(fieldName, group)) {
      errors.push(Messages.validation.requiredValidation());
    }

    form.setFieldState(
      errors && errors?.length > 0 ? ValidationState.INVALID : ValidationState.VALID,
      fieldName,
      group,
    );

    setErrorMessages(errors);
  };

  React.useEffect(() => {
    if (validationMode === FormValidationMode.ON || groupValidationMode[group] === FormValidationMode.ON) {
      if (required) {
        fieldValidation();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationMode, groupValidationMode]);

  return (
    <LabelWrapper
      errors={errorMessages}
      fieldName={fieldName}
      layout={layout}
      label={label}
      required={required}
      tooltip={tooltip}
      subField={subField}
      statusInfo={statusInfo}
      testId={testId}
    >
      <ChoiceGroup
        id={fieldName}
        data-test-id={radioGroupTestIds.component}
        options={radioGroupOptions}
        defaultSelectedKey={defaultValue}
        onChange={internalOnChange}
        styles={radioGroupStyles}
      />
    </LabelWrapper>
  );
};
